// Facilities Overview: compare up to 4 games side by side, in the same layout as the concessions,
// retail and gates Overviews (game chips + time window, headline numbers, findings, game summaries,
// a restroom comparison and one chart through the day).
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import AuthContext from '../../contexts/auth-context.js';
import { getTeam } from '../../util.js';
import Icon from '../../components/atoms/Icon.js';
import { Panel, PanelHeader, InfoTip } from '../Concession/components/ConcessionUI';
import { ChartSegmented } from '../Concession/charts/chartTheme';
import { KpiCard } from '../Concession/live/LiveComponents';
import Presence from '../Concession/components/Presence';
import OverviewInsights from '../Concession/components/OverviewInsights';
import { TimeWindow } from '../Concession/ConcessionOverview';
import OverviewCompareChart from '../Retail/overview/OverviewCompareChart';
import { bucketSeries } from '../Retail/overview/overviewMetrics';
import RestroomIcon from './components/RestroomIcon';
import { fetchFacilitiesDay } from './facilitiesApi';
import { buildRestroomGameMetrics, buildFacilitiesOverviewInsights, midOffset, relativeToStart } from './facilitiesOverviewMetrics';
import { CLEANING_THRESHOLD, minuteToClock, queueColor } from './facilitiesMetrics';
import '../Concession/concession.css';
import '../Retail/retail.css';
import './facilities.css';

const MAX_GAMES = 4;

// Same game colors as the concessions and retail Overviews
const overviewColors = [
  '#3e58c0', '#e88559', '#9792ec', '#aa4851', '#866263', '#76ba92', '#0bcaca', '#f1dc78', '#3c6e5f',
];

const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '--');

const SeasonSelector = ({ games, currentSeason, setCurrentSeason }) => {
  const seasons = useMemo(() => {
    const years = new Set();
    games.forEach((game) => game.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
    return Array.from(years).sort((a, b) => b - a);
  }, [games]);
  return (
    <div className="cx-season">
      <span className="cx-chart-segment-label">Season</span>
      <div className="cx-chart-segmented">
        {seasons.map((season) => (
          <button
            key={season}
            type="button"
            className={`cx-chart-segment ${currentSeason === season ? 'active' : ''}`}
            onClick={() => setCurrentSeason(season)}
          >
            {season}
          </button>
        ))}
      </div>
    </div>
  );
};

const RESTROOM_METRICS = [
  { value: 'visits', label: 'Entries' },
  { value: 'dueCount', label: 'Cleanings due' },
  { value: 'queue', label: 'Peak queue' },
];

export default function FacilitiesOverview({ currentLocation, games }) {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [currentSeason, setCurrentSeason] = useState(() => {
    const years = new Set();
    games.forEach((game) => game.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
    return Array.from(years).sort((a, b) => b - a)[0] || moment().format('YYYY');
  });

  // Selected games keep their color while selected. Data is stored by date, so a slow
  // response can never land on the wrong game.
  const [selected, setSelected] = useState([]); // [{ date, color }]
  const [dataByDate, setDataByDate] = useState({});
  const [timeRange, setTimeRange] = useState({ startOffset: -180, endOffset: 240 });
  const [pending, setPending] = useState([]);
  const loading = pending.length > 0;

  // "Add game" dropdown
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setPickerOpen(false);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);
  useEffect(() => {
    if (selected.length >= MAX_GAMES) setPickerOpen(false);
  }, [selected.length]);

  const findGameByDate = useCallback((date) => {
    for (const series of games) {
      const index = series.dates.findIndex((d) => d.date === date);
      if (index !== -1) return { series, gameDate: series.dates[index], index };
    }
    return null;
  }, [games]);

  const gameLabel = useCallback((date) => {
    const found = findGameByDate(date);
    const opponent = found ? getTeam(currentLocation.league, found.series.teamID)?.name : null;
    return {
      short: moment(date).format('MMM D'),
      long: moment(date).format('ddd, MMM D'),
      sub: found ? `${opponent ? `vs ${opponent} · ` : ''}Game ${found.index + 1}` : '',
      opponent,
    };
  }, [findGameByDate, currentLocation.league]);

  const fetchGame = (date) => {
    setPending((p) => [...p, date]);
    fetchFacilitiesDay(currentLocation, authCtx, date)
      .then((restrooms) => setDataByDate((prev) => ({ ...prev, [date]: restrooms })))
      .catch((error) => {
        if (error?.status === 401) {
          authCtx.logout();
          navigate('/auth');
        } else {
          alert('Unable to fetch data. Please refresh the page and try again.');
        }
      })
      .finally(() => setPending((p) => p.filter((d) => d !== date)));
  };

  const toggleGame = (date) => {
    if (selected.some((s) => s.date === date)) {
      setSelected((prev) => prev.filter((s) => s.date !== date));
      return;
    }
    if (selected.length >= MAX_GAMES) {
      alert('Maximum amount of games is 4.');
      return;
    }
    const used = selected.map((s) => s.color);
    const color = overviewColors.find((c) => !used.includes(c)) || overviewColors[0];
    setSelected((prev) => [...prev, { date, color }]);
    if (!dataByDate[date]) fetchGame(date);
  };

  const filteredGames = games
    .filter((x) => new Date() > moment(x.dates[0].date))
    .filter((x) => moment(x.dates[0].date).format('YYYY') === currentSeason);

  // Each loaded game's numbers, recalculated when the time window changes
  const metricsByDate = useMemo(() => {
    const out = {};
    Object.entries(dataByDate).forEach(([date, restrooms]) => {
      out[date] = buildRestroomGameMetrics({ restrooms, date, gameDate: findGameByDate(date)?.gameDate, window: timeRange });
    });
    return out;
  }, [dataByDate, timeRange, findGameByDate]);

  const cards = selected.map((s) => ({
    ...s,
    label: gameLabel(s.date),
    pending: pending.includes(s.date),
    m: metricsByDate[s.date] || null,
  }));
  const ready = cards.filter((c) => c.m && c.m.hasData);
  const hasSelection = selected.length > 0;
  const gamesWithNoData = cards.filter((c) => c.m && !c.m.hasData);

  // Headline numbers, averaged per game
  const avgOf = (values) => {
    const v = values.filter((x) => typeof x === 'number');
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  const kpi = useMemo(() => {
    const rushes = ready.filter((c) => c.m.rush);
    const rushOffsets = rushes.filter((c) => c.m.startMinute != null).map((c) => midOffset(c.m.rush, c.m.startMinute));
    // Busiest restroom across the games combined
    const totals = new Map();
    ready.forEach((c) => c.m.perRestroom.forEach((r) => {
      const t = totals.get(r.id) || { label: r.label, visits: 0 };
      t.visits += r.visits;
      totals.set(r.id, t);
    }));
    const busiest = [...totals.values()].sort((a, b) => b.visits - a.visits)[0] || null;
    const totalVisits = ready.reduce((sum, c) => sum + c.m.visits, 0);
    const peakQueue = ready.reduce((top, c) => (
      c.m.peakRestroom && (!top || c.m.peakRestroom.queuePeak.value > top.value)
        ? { ...c.m.peakRestroom.queuePeak, restroom: c.m.peakRestroom.label, label: c.label }
        : top
    ), null);
    return {
      peakQueue,
      avgQueue: avgOf(ready.map((c) => c.m.avgQueue)),
      anyQueue: ready.some((c) => c.m.hasQueue),
      visits: avgOf(ready.map((c) => c.m.visits)),
      totalVisits,
      due: avgOf(ready.map((c) => c.m.dueCount)),
      totalDue: ready.reduce((sum, c) => sum + c.m.dueCount, 0),
      rush: avgOf(rushes.map((c) => c.m.rush.total)),
      rushOffset: avgOf(rushOffsets),
      busiest: busiest && busiest.visits > 0 ? { ...busiest, share: totalVisits ? Math.round((busiest.visits / totalVisits) * 100) : 0 } : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, metricsByDate]);

  const insights = useMemo(() => buildFacilitiesOverviewInsights(ready), [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bold the highest game per row (only a clear winner, only when comparing)
  const bestOf = (pick) => {
    if (ready.length < 2) return null;
    const values = ready.map((c) => ({ date: c.date, v: pick(c.m) })).filter((x) => typeof x.v === 'number');
    if (values.length < 2) return null;
    const max = Math.max(...values.map((x) => x.v));
    const top = values.filter((x) => x.v === max);
    return top.length === 1 ? top[0].date : null;
  };
  const best = {
    visits: bestOf((m) => m.visits),
    due: bestOf((m) => m.dueCount),
    rush: bestOf((m) => m.rush?.total),
    queue: bestOf((m) => m.peakRestroom?.queuePeak.value),
  };

  // Restrooms across the games
  const [restroomMetric, setRestroomMetric] = useState('visits');
  const restroomRows = useMemo(() => {
    if (!ready.length) return [];
    const ids = Array.from(new Set(ready.flatMap((c) => c.m.perRestroom.map((r) => r.id))));
    const rows = ids.map((id) => {
      const info = ready.flatMap((c) => c.m.perRestroom).find((r) => r.id === id);
      const cells = ready.map((c) => {
        const r = c.m.perRestroom.find((x) => x.id === id);
        if (!r) return { date: c.date, v: null };
        if (restroomMetric === 'queue') return { date: c.date, v: r.queuePeak ? r.queuePeak.value : null, minute: r.queuePeak?.minute };
        const total = restroomMetric === 'visits' ? c.m.visits : c.m.dueCount;
        return { date: c.date, v: r[restroomMetric], share: total ? Math.round((r[restroomMetric] / total) * 100) : 0 };
      });
      const vals = cells.map((x) => x.v).filter((v) => v != null);
      return { id, label: info?.label, type: info?.type, imageURL: info?.imageURL, cells, mean: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
    });
    return rows.sort((a, b) => (b.mean ?? -1) - (a.mean ?? -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, metricsByDate, restroomMetric]);
  const rowMax = Math.max(1, ...restroomRows.flatMap((r) => r.cells.map((x) => x.v || 0)));
  const anyQueue = ready.some((c) => c.m.hasQueue);
  const restroomMetrics = anyQueue ? RESTROOM_METRICS : RESTROOM_METRICS.filter((o) => o.value !== 'queue');

  // Chart through the day
  const metricOptions = [
    { value: 'visits', label: 'Entries' },
    ...(ready.some((c) => c.m.hasQueue) ? [{ value: 'queue', label: 'Queues' }] : []),
  ];
  const [metric, setMetric] = useState('visits');
  // Entries as a running total (default, like gates) or per interval
  const [countMode, setCountMode] = useState('total');
  const [align, setAlign] = useState('start');
  const [resolution, setResolution] = useState(15);
  const chart = useMemo(() => {
    const isQueue = metric === 'queue';
    const isTotal = !isQueue && countMode === 'total';
    const series = ready.map((c) => {
      let points = isQueue
        ? bucketSeries(c.m.queuePoints, { align, startMinute: c.m.startMinute, size: resolution, agg: 'avg' })
        : bucketSeries(c.m.visitsPoints, { align, startMinute: c.m.startMinute, size: isTotal ? 1 : resolution, agg: 'sum' });
      if (isTotal) {
        // Running total from the start of the window, as of the end of each interval
        let sum = 0;
        points = points.map((p) => {
          sum += p.y;
          return { x: p.x, y: sum };
        });
      }
      return { key: c.date, label: `${c.label.short}${c.label.opponent ? ` · ${c.label.opponent}` : ''}`, color: c.color, points };
    });
    const every = resolution === 60 ? 'hour' : resolution === 1 ? 'minute' : `${resolution} min`;
    const avgLabel = resolution === 60 ? 'hourly averages' : resolution === 1 ? 'minute by minute' : `${resolution}-minute averages`;
    const meta = isQueue
      ? { title: 'Queues', unit: 'min', yTitle: 'Queue (min)', subtitle: `Average queue across the restrooms, ${avgLabel}.` }
      : isTotal
      ? { title: 'Entries', unit: 'entries', yTitle: 'Entries so far', subtitle: 'Running total of entries to every restroom, from the start of the time window.' }
      : { title: 'Entries', unit: 'entries', yTitle: `Entries per ${every}`, subtitle: `Entries to every restroom, per ${every}. Shows when each rush happened.` };
    const startMarkers = ready.filter((c) => c.m.startMinute != null).map((c) => ({ minute: c.m.startMinute, color: c.color }));
    return { series, startMarkers, ...meta };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, metricsByDate, metric, align, resolution, countMode]);

  return (
    <div className="rx-ov">
      {loading && (
        <div style={{ position: 'fixed', top: 0, left: 75, right: 0, zIndex: 9999 }}>
          <LinearProgress
            sx={{
              backgroundColor: 'rgba(2, 166, 147, 0.25)',
              height: 4,
              '& .MuiLinearProgress-bar': { backgroundColor: 'rgb(2, 166, 147)' },
            }}
          />
        </div>
      )}

      {/* Compare bar: chips for the selected games + Add game dropdown, and the time window */}
      <div className="mb-4 cx-ov-bar-wrap">
        <Panel className="cx-ov-bar">
          <div className="cx-ov-bar-row">
            <div className="cx-ov-bar-games">
              <div className="cx-ov-bar-label">
                Games
                <span className="rx-ov-count">{selected.length} of {MAX_GAMES}</span>
              </div>
              <div className="cx-ov-chips">
                {cards.map((c) => (
                  <span key={c.date} className={`cx-ov-chip cx-fade-in${c.pending ? ' pending' : ''}`}>
                    <span className="cx-ov-swatch" style={{ background: c.color }} />
                    <span className="cx-ov-chip-text rx-ov-chip-text">
                      <span>
                        {c.label.short}
                        {c.label.opponent && <span className="cx-ov-chip-sub"> · {c.label.opponent}</span>}
                      </span>
                      {c.m && c.m.windowStart != null && (
                        <span className="rx-ov-chip-window">{minuteToClock(c.m.windowStart)} – {minuteToClock(c.m.windowEnd)}</span>
                      )}
                    </span>
                    {c.pending && <span className="cx-report-spinner cx-ov-chip-spinner" aria-hidden="true" />}
                    <button type="button" className="cx-ov-chip-remove" aria-label={`Remove ${c.label.short}`} onClick={() => toggleGame(c.date)}>
                      <Icon name="x" />
                    </button>
                  </span>
                ))}
                <div className="cx-ov-add" ref={pickerRef}>
                  <button
                    type="button"
                    className="cx-ov-add-btn"
                    onClick={() => setPickerOpen((o) => !o)}
                    disabled={selected.length >= MAX_GAMES && !pickerOpen}
                    aria-expanded={pickerOpen}
                  >
                    <Icon name="plus-lg" />
                    {selected.length >= MAX_GAMES ? 'Max 4 games' : hasSelection ? 'Add game' : 'Add games to compare'}
                  </button>
                  {pickerOpen && (
                    <div className="cx-ov-picker" role="dialog" aria-label="Pick games">
                      <div className="cx-ov-picker-head">
                        <span>Pick up to 4 games</span>
                        <button type="button" className="cx-ov-chip-remove" aria-label="Close" onClick={() => setPickerOpen(false)}>
                          <Icon name="x-lg" />
                        </button>
                      </div>
                      <SeasonSelector games={games} currentSeason={currentSeason} setCurrentSeason={setCurrentSeason} />
                      <div className="cx-game-list">
                        {filteredGames.map((game, gameIndex) => {
                          const startDate = game.dates?.[0]?.date;
                          const endDate = game.dates?.[game.dates.length - 1]?.date;
                          return (
                            <div key={`game-${game.teamID}-${gameIndex}`} className="cx-game-row">
                              <div className="cx-game-info">
                                <img alt="Opponent" width={22} height={22} src={getTeam(currentLocation.league, game.teamID)?.imgURL} />
                                <span className="cx-game-name">{getTeam(currentLocation.league, game?.teamID)?.name}</span>
                                <span className="cx-game-dates">
                                  {moment(startDate).format('MMM D')} - {moment(endDate).format('MMM D')}
                                </span>
                              </div>
                              <div className="cx-game-buttons">
                                {game.dates.map((gameDate, dateIndex) => {
                                  if (!(new Date() > moment(gameDate.date))) return null;
                                  const sel = selected.find((s) => s.date === gameDate.date);
                                  return (
                                    <button
                                      key={gameDate.date}
                                      type="button"
                                      onClick={() => toggleGame(gameDate.date)}
                                      className={`btn cx-game-btn${sel ? ' selected' : ''}`}
                                      style={sel ? { backgroundColor: sel.color, borderColor: sel.color, color: '#ffffff' } : undefined}
                                    >
                                      {dateIndex + 1}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {hasSelection && (
                  <button type="button" className="cx-ov-clear" onClick={() => setSelected([])}>
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="cx-ov-bar-window">
              <TimeWindow
                compact
                startOffset={timeRange.startOffset}
                endOffset={timeRange.endOffset}
                onStartChange={(v) => setTimeRange((t) => ({ ...t, startOffset: v }))}
                onEndChange={(v) => setTimeRange((t) => ({ ...t, endOffset: v }))}
                onPreset={(start, end) => setTimeRange({ startOffset: start, endOffset: end })}
                info={(
                  <InfoTip
                    title="Time window"
                    text="Every number, finding and chart below uses this window, measured from each game's start."
                  />
                )}
              />
            </div>
          </div>
        </Panel>
      </div>

      <Presence
        show={hasSelection}
        className="cx-ov-presence"
        fallback={(
          <div className="cx-empty-state cx-ov-empty-main">
            <Icon name="bar-chart-line" />
            <div className="cx-empty-title">No games selected</div>
            <div>Use <strong>Add games to compare</strong> above to pick up to 4 games and compare restroom entries and cleanings due side by side.</div>
          </div>
        )}
      >
        <div className="rx-ov-main">
          {gamesWithNoData.length > 0 && (
            <div className="cx-notice">
              <Icon name="info-circle" />
              No restroom data for {gamesWithNoData.map((c) => c.label.short).join(', ')}.
            </div>
          )}

          {/* Headline numbers */}
          {ready.length > 0 && (
            <div className="cx-kpi-grid cx-kpi-grid-auto">
              <KpiCard label="Avg entries" info="Entries to every restroom during the time window, averaged per game." value={fmt(kpi.visits)}>
                <div className="cx-kpi-sub">{fmt(kpi.totalVisits)} across {ready.length} game{ready.length === 1 ? '' : 's'}</div>
                <div className="cx-kpi-foot">Per game, averaged</div>
              </KpiCard>
              <KpiCard
                label="Avg cleanings due"
                info={`There are no cleaning records yet, so a cleaning is counted as due each time a restroom reaches another ${CLEANING_THRESHOLD} entries. Averaged per game.`}
                value={kpi.due != null ? (Math.round(kpi.due * 10) / 10).toLocaleString() : '—'}
              >
                <div className="cx-kpi-sub">{fmt(kpi.totalDue)} across {ready.length} game{ready.length === 1 ? '' : 's'}</div>
                <div className="cx-kpi-foot">One every {CLEANING_THRESHOLD} entries per restroom</div>
              </KpiCard>
              <KpiCard
                label="Busiest 15 min"
                info="The quarter-hour with the most entries across every restroom, averaged per game."
                value={kpi.rush != null ? <>{fmt(kpi.rush)}<span className="cx-unit">entries</span></> : '—'}
              >
                {kpi.rushOffset != null && <div className="cx-kpi-sub">Typically {relativeToStart(kpi.rushOffset)}</div>}
                <div className="cx-kpi-foot">Per game, averaged</div>
              </KpiCard>
              {kpi.anyQueue ? (
              <KpiCard
                label="Peak queue"
                info="The longest queue reading at any restroom during the time window, across the selected games."
                value={kpi.peakQueue ? <>{kpi.peakQueue.value}<span className="cx-unit">min</span></> : '—'}
                accent={kpi.peakQueue ? queueColor(kpi.peakQueue.value) : undefined}
              >
                {kpi.peakQueue && (
                  <div className="cx-kpi-sub">{kpi.peakQueue.restroom} ({kpi.peakQueue.label.long}, {minuteToClock(kpi.peakQueue.minute)})</div>
                )}
                <div className="cx-kpi-foot">{kpi.avgQueue != null ? `Avg queue ${kpi.avgQueue.toFixed(1)} min across restrooms` : 'No queue readings'}</div>
              </KpiCard>
              ) : (
              <KpiCard
                label="Busiest restroom"
                info="The restroom with the most entries across the selected games."
                value={kpi.busiest ? <span className="fx-kpi-text">{kpi.busiest.label}</span> : '—'}
              >
                {kpi.busiest && <div className="cx-kpi-sub">{fmt(kpi.busiest.visits)} entries · {kpi.busiest.share}% of all</div>}
                <div className="cx-kpi-foot">Across the selected games</div>
              </KpiCard>
              )}
            </div>
          )}

          {insights.length > 0 && (
            <div className="mb-4">
              <Panel>
                <PanelHeader title="What stands out" subtitle={`Key findings across the selected game${ready.length === 1 ? '' : 's'}, most important first.`} />
                <OverviewInsights insights={insights} />
              </Panel>
            </div>
          )}

          {/* One card per game */}
          <Panel className="rx-ov-panel">
            <PanelHeader
              title="Game summaries"
              subtitle={ready.length > 1 ? 'Each selected game. The highest value in each row is bold.' : 'Each selected game.'}
            />
            <div className="rx-ov-games">
              {cards.map((c) => (
                <div key={c.date} className="rx-ov-game cx-fade-in" style={{ '--game-color': c.color }}>
                  <div className="rx-ov-game-head">
                    <span className="cx-ov-swatch" style={{ background: c.color }} />
                    <div>
                      <div className="rx-ov-game-date">{c.label.long}</div>
                      {c.label.sub && <div className="rx-ov-game-sub">{c.label.sub}</div>}
                    </div>
                  </div>
                  {!c.m ? (
                    <div className="rx-ov-game-loading">
                      <div className="cx-skel cx-skel-value" />
                      <div className="cx-skel cx-skel-line" />
                      <div className="cx-skel cx-skel-line short" />
                    </div>
                  ) : !c.m.hasData ? (
                    <div className="rx-ov-na">No restroom data for this game.</div>
                  ) : (
                    <>
                      <div className={`rx-ov-game-big${best.visits === c.date ? ' best' : ''}`}>
                        {fmt(c.m.visits)}
                        <span>entries</span>
                      </div>
                      <dl className="rx-ov-rows">
                        <div className={best.due === c.date ? 'best' : ''}>
                          <dt>Cleanings due</dt>
                          <dd>{fmt(c.m.dueCount)}</dd>
                        </div>
                        {c.m.hasQueue && (
                          <div className={best.queue === c.date ? 'best' : ''}>
                            <dt>Peak queue</dt>
                            <dd>
                              {c.m.peakRestroom ? (
                                <>
                                  <span style={{ color: queueColor(c.m.peakRestroom.queuePeak.value) }}>{c.m.peakRestroom.queuePeak.value} min</span>
                                  <small> · {c.m.peakRestroom.label}, {minuteToClock(c.m.peakRestroom.queuePeak.minute)}</small>
                                </>
                              ) : '—'}
                            </dd>
                          </div>
                        )}
                        <div className={best.rush === c.date ? 'best' : ''}>
                          <dt>Busiest 15 min</dt>
                          <dd>
                            {c.m.rush ? <>{fmt(c.m.rush.total)}<small> · {minuteToClock(c.m.rush.start)}</small></> : '—'}
                          </dd>
                        </div>
                        <div>
                          <dt>Before game start</dt>
                          <dd>{c.m.beforeStartShare != null ? `${c.m.beforeStartShare}%` : '—'}</dd>
                        </div>
                        <div>
                          <dt>Busiest restroom</dt>
                          <dd className="rx-ov-zone">
                            {c.m.busiest ? <>{c.m.busiest.label}<small> · {Math.round((c.m.busiest.visits / c.m.visits) * 100)}%</small></> : '—'}
                          </dd>
                        </div>
                      </dl>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          {/* Restrooms across the games */}
          {restroomRows.length > 0 && (
            <Panel className="rx-ov-panel">
              <PanelHeader
                title="Restrooms across the games"
                subtitle={restroomMetric === 'visits'
                  ? "Each restroom's entries and share in each game. Busiest restrooms first."
                  : restroomMetric === 'queue'
                    ? "Each restroom's longest queue in each game. Longest queues first."
                    : `Cleanings due at each restroom in each game (one every ${CLEANING_THRESHOLD} entries). Most first.`}
                actions={<ChartSegmented options={restroomMetrics} value={restroomMetric} onChange={setRestroomMetric} />}
              />
              <div className="fx-ov-table-wrap">
                <table className="fx-ov-table">
                  <thead>
                    <tr>
                      <th className="fx-ov-name-col">Restroom</th>
                      {ready.map((c) => (
                        <th key={c.date}>
                          <span className="cx-ov-swatch" style={{ background: c.color }} /> {c.label.short}
                        </th>
                      ))}
                      {ready.length > 1 && <th>Average</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {restroomRows.map((r) => (
                      <tr key={r.id}>
                        <td className="fx-ov-name-col">
                          <span className="fx-ov-name">
                            <RestroomIcon imageURL={r.imageURL} />
                            {r.label}
                          </span>
                        </td>
                        {r.cells.map((x) => (
                          <td key={x.date}>
                            {x.v == null ? <span className="fx-ov-none">—</span> : restroomMetric === 'queue' ? (
                              <>
                                <strong style={{ color: queueColor(x.v) }}>{x.v} min</strong>
                                <small>{minuteToClock(x.minute)}</small>
                              </>
                            ) : (
                              <>
                                <strong>{fmt(x.v)}</strong>
                                <span className="fx-ov-bar">
                                  <i style={{ width: `${(x.v / rowMax) * 100}%`, background: restroomMetric === 'visits' ? '#3e58c0' : '#d97706' }} />
                                </span>
                                {restroomMetric === 'visits' && <small>{x.share}%</small>}
                              </>
                            )}
                          </td>
                        ))}
                        {ready.length > 1 && (
                          <td className="fx-ov-avg">
                            {r.mean == null ? '—' : restroomMetric === 'queue' ? `${Math.round(r.mean)} min` : restroomMetric === 'visits' ? fmt(r.mean) : (Math.round(r.mean * 10) / 10).toLocaleString()}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {/* Through the day */}
          {ready.length > 0 && (
            <OverviewCompareChart
              metricOptions={metricOptions}
              metric={metric}
              onMetric={setMetric}
              align={align}
              onAlign={setAlign}
              series={chart.series}
              unit={chart.unit}
              yTitle={chart.yTitle}
              subtitle={chart.subtitle}
              startMarkers={chart.startMarkers}
              title={chart.title}
              resolution={resolution}
              onResolution={metric === 'queue' || countMode === 'interval' ? setResolution : undefined}
              extraControls={metric !== 'queue' ? (
                <ChartSegmented
                  options={[
                    { value: 'total', label: 'Running total' },
                    { value: 'interval', label: 'Per interval' },
                  ]}
                  value={countMode}
                  onChange={setCountMode}
                />
              ) : null}
            />
          )}
        </div>
      </Presence>
    </div>
  );
}
